import { ListItem, ListSection } from '@/components/list';
import { Form, FormItem, FormSection } from '@/components/nativewind-ui/form';
import { TextField } from '@/components/nativewind-ui/text-field';
import { Toggle } from '@/components/nativewind-ui/toggle';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Text } from '@/components/ui/text';
import { useAuth } from '@/contexts/AuthContext';
import { X } from '@/icons/X';
import { generateAPIUrl } from '@/utils/api-url';
import { cn } from '@/utils/cn';
import { router } from 'expo-router';
import Head from 'expo-router/head';
import * as WebBrowser from 'expo-web-browser';
import { fetch as expoFetch } from 'expo/fetch';
import { hc } from 'hono/client';
import { Fragment, useCallback, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { ApiAuthType } from '../../../api/auth/[...everything]+api';
import type { ApiUserType } from '../../../api/user/[...everything]+api';

const userClient = hc<ApiUserType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

const authClient = hc<ApiAuthType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

async function updateUser(
  token: string,
  updates: { name?: string; debugMode?: boolean }
) {
  const res = await userClient.api.user.me.$put(
    { json: updates },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!res.ok) {
    throw new Error('Failed to update user');
  }

  const result = await res.json();

  if (result.valid !== true) {
    throw new Error('Failed to update user');
  }

  return true;
}

async function deleteUser(token: string) {
  const res = await userClient.api.user.me.$delete(
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!res.ok) {
    throw new Error('Failed to delete user');
  }

  const result = await res.json();

  if (result.valid !== true) {
    throw new Error('Failed to delete user');
  }

  return true;
}

async function logUserOut(token: string) {
  const res = await authClient.api.auth.$delete(
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!res.ok) {
    throw new Error('Failed to log out');
  }

  const result = await res.json();

  if (result.valid !== true) {
    throw new Error('Failed to log out');
  }

  return true;
}

export default function Account() {
  const { logOut, user, authState, updateAuthState } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);

  const openURL = useCallback((url: string) => {
    if (Platform.OS === 'web') {
      Linking.openURL(url);
    } else {
      WebBrowser.openBrowserAsync(url);
    }
  }, []);

  const handleLogout = async () => {
    if (!authState.token) {
      alert('You are not logged in');
      return;
    }

    setIsLoggingOut(true);
    try {
      await logUserOut(authState.token);
      logOut?.();
    } catch (error) {
      console.error(error);
      alert('Failed to log out');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!authState.token) {
      alert('You are not logged in');
      return;
    }

    setIsLoggingOut(true);
    setShowDeleteConfirm(false);
    try {
      await deleteUser(authState.token);
      logOut?.();
    } catch (error) {
      console.error(error);
      alert('Failed to delete account');
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <Fragment>
      <Head>
        <title>Account - Lexikon AI</title>
      </Head>

      <SafeAreaView className="flex-1">
        <ScrollView
          contentInsetAdjustmentBehavior="automatic"
          className="flex-1"
        >
          <View className="mx-2 my-4">
            {isLoggingOut ? (
              <ActivityIndicator size="large" color="#0f1313" />
            ) : (
              <Fragment>
                <ListSection title="Account">
                  <ListItem
                    title="Name"
                    value={user?.name || 'Your Name'}
                    isFirst
                    onPress={() => setShowNameModal(true)}
                  />
                  <ListItem
                    title="Manage Subscription"
                    isLast
                    onPress={() =>
                      router.navigate(
                        user?.planKeyname === 'free'
                          ? '/upgrade'
                          : '/account/subscription'
                      )
                    }
                  />
                </ListSection>

                <ListSection title="Support">
                  <ListItem
                    title="Help & Feedback"
                    isFirst
                    onPress={() => router.navigate('/feedback')}
                  />
                  <ListItem
                    title="Terms of Service"
                    onPress={() => openURL('https://lexikon.ai/terms')}
                  />
                  <ListItem
                    title="Privacy Policy"
                    onPress={() => openURL('https://lexikon.ai/privacy')}
                  />
                  <ListItem
                    title="Debug Mode"
                    value={user?.debugMode ? 'On' : 'Off'}
                    isLast
                    onPress={() => setShowDebugModal(true)}
                  />
                </ListSection>

                <ListSection title="Danger Zone" modifier="danger">
                  <ListItem title="Log Out" isFirst onPress={handleLogout} />
                  <ListItem
                    title="Delete Account"
                    isLast
                    onPress={() => setShowDeleteConfirm(true)}
                    modifier="danger"
                  />
                </ListSection>
              </Fragment>
            )}
          </View>
        </ScrollView>

        <DeleteConfirmModal
          isVisible={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteAccount}
        />

        <NameEditModal
          isVisible={showNameModal}
          onClose={(newName: string | null) => {
            setShowNameModal(false);
            if (newName) {
              updateAuthState?.({
                authState,
                user: user ? { ...user, name: newName } : undefined
              });
            }
          }}
          initialName={user?.name || ''}
        />

        <DebugModeModal
          isVisible={showDebugModal}
          onClose={(debugEnabled: boolean) => {
            setShowDebugModal(false);
            updateAuthState?.({
              authState,
              user: user ? { ...user, debugMode: debugEnabled } : undefined
            });
          }}
        />
      </SafeAreaView>
    </Fragment>
  );
}

function LeftLabel({
  children,
  isWide
}: {
  children: string;
  isWide?: boolean;
}) {
  return (
    <View className={cn('justify-center pl-2', isWide ? 'w-32' : 'w-28')}>
      <Text className="font-medium">{children}</Text>
    </View>
  );
}

interface NameEditModalProps {
  isVisible: boolean;
  onClose: (newName: string | null) => void;
  initialName: string;
}

function NameEditModal({
  isVisible,
  onClose,
  initialName
}: NameEditModalProps) {
  const [name, setName] = useState(initialName);
  const [isSaving, setIsSaving] = useState(false);
  const { authState } = useAuth();

  const saveName = useCallback(async () => {
    if (!name.trim()) {
      alert('Name cannot be empty');
      return;
    }

    if (!authState.token) {
      alert('User not authenticated');
      return;
    }

    setIsSaving(true);
    try {
      await updateUser(authState.token, { name });
      onClose(name);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [name, authState.token]);

  return (
    <Modal animationType="slide" transparent visible={isVisible}>
      <View className="absolute bottom-0 h-[60%] w-full rounded-t-[18px] bg-[#25292e]">
        <View className="h-20 flex-row items-center justify-between rounded-t-[10px] bg-[#464C55] px-5">
          <View className="w-8" />
          <Text className="text-lg font-medium">Edit Name</Text>
          <Pressable onPress={() => onClose(null)}>
            <X color="#fff" size={22} />
          </Pressable>
        </View>

        <SafeAreaView style={styles.keyboardAvoidingViewStyles}>
          <KeyboardAvoidingView
            behavior="padding"
            style={styles.keyboardAvoidingViewStyles}
          >
            <ScrollView className="flex-1">
              <Form className="gap-5 px-4 pt-8">
                <FormSection>
                  <FormItem>
                    <TextField
                      autoFocus
                      label={Platform.select({
                        ios: undefined,
                        default: 'Name'
                      })}
                      leftView={Platform.select({
                        ios: <LeftLabel isWide>Name</LeftLabel>
                      })}
                      placeholder="Enter your name"
                      value={name}
                      onChangeText={setName}
                      onSubmitEditing={saveName}
                      submitBehavior="submit"
                      enterKeyHint="done"
                    />
                  </FormItem>
                </FormSection>

                <Button
                  className="rounded-full"
                  onPress={saveName}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <View className="py-1">
                      <ActivityIndicator color="black" />
                    </View>
                  ) : (
                    <Text>Save</Text>
                  )}
                </Button>
              </Form>
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  keyboardAvoidingViewStyles: {
    flex: 1
  }
});

interface DeleteConfirmModalProps {
  isVisible: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function DeleteConfirmModal({
  isVisible,
  onClose,
  onConfirm
}: DeleteConfirmModalProps) {
  return (
    <Modal animationType="slide" transparent visible={isVisible}>
      <View className="absolute bottom-0 w-full rounded-t-[18px] bg-[#25292e]">
        <View className="h-20 flex-row items-center justify-between rounded-t-[10px] bg-[#464C55] px-5">
          <View className="w-8" />
          <Text className="text-lg font-medium">Delete Account</Text>
          <Pressable onPress={onClose}>
            <X color="#fff" size={22} />
          </Pressable>
        </View>

        <View className="p-6">
          <Text className="mb-6 text-center">
            Are you sure you want to delete your account? This action cannot be
            undone. Any active subscriptions will be canceled immediately.
          </Text>
          <Button
            variant="destructive"
            className="mb-3 rounded-full"
            onPress={onConfirm}
          >
            <Text>Delete Account</Text>
          </Button>
          <Button className="rounded-full" onPress={onClose}>
            <Text>Cancel</Text>
          </Button>
        </View>
      </View>
    </Modal>
  );
}

interface DebugModeModalProps {
  isVisible: boolean;
  onClose: (debugEnabled: boolean) => void;
}

function DebugModeModal({ isVisible, onClose }: DebugModeModalProps) {
  const { authState, user } = useAuth();
  const [debugEnabled, setDebugEnabled] = useState(!!user?.debugMode);
  const [isSaving, setIsSaving] = useState(false);

  const saveDebugMode = useCallback(async () => {
    if (!authState.token) {
      alert('User not authenticated');
      return;
    }

    setIsSaving(true);
    try {
      await updateUser(authState.token, { debugMode: debugEnabled });
      onClose(debugEnabled);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [debugEnabled, authState.token]);

  const toggleDebugMode = () => {
    setDebugEnabled(!debugEnabled);
  };

  return (
    <Modal animationType="slide" transparent visible={isVisible}>
      <View className="absolute bottom-0 w-full rounded-t-[18px] bg-[#25292e]">
        <View className="h-20 flex-row items-center justify-between rounded-t-[10px] bg-[#464C55] px-5">
          <View className="w-8" />
          <Text className="text-lg font-medium">Debug Mode</Text>
          <Pressable onPress={() => onClose(!!user?.debugMode)}>
            <X color="#fff" size={22} />
          </Pressable>
        </View>

        <View className="p-6">
          <View className="mb-6 flex-row items-center justify-between">
            <Text>Enable Debug Mode</Text>
            <Toggle
              aria-labelledby="debug-mode-lbl"
              value={debugEnabled}
              onValueChange={toggleDebugMode}
            />
          </View>
          <Label id="debug-mode-lbl" className="mb-3 text-gray-400">
            Debug mode provides additional logging and diagnostic information to
            help troubleshoot issues.
          </Label>
          <Button
            className="mb-3 rounded-full"
            onPress={saveDebugMode}
            disabled={isSaving}
          >
            {isSaving ? (
              <View className="py-1">
                <ActivityIndicator color="black" />
              </View>
            ) : (
              <Text>{debugEnabled ? 'Enable' : 'Disable'}</Text>
            )}
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onPress={() => onClose(!!user?.debugMode)}
          >
            <Text>Cancel</Text>
          </Button>
        </View>
      </View>
    </Modal>
  );
}
